<!--
 * @Author: wjc
 * @Date: 2026-08-13 10:00:00
 * @Description: 全站搜索命令面板，基于 reka-ui Dialog + Combobox
-->
<template>
  <DialogRoot v-model:open="open">
    <DialogTrigger as-child>
      <slot>
        <button class="search-trigger" type="button">
          <i class="i-custom-search" aria-hidden="true"></i>
          <span>全站搜索</span>
        </button>
      </slot>
    </DialogTrigger>
    <DialogPortal>
      <Transition name="overlay">
        <DialogOverlay class="search-overlay" />
      </Transition>
      <Transition name="content">
        <DialogContent class="search-panel" @escape-key-down="close">
          <DialogTitle class="sr-only">全站搜索</DialogTitle>
          <DialogDescription class="sr-only">输入关键词后选择结果跳转</DialogDescription>
          <ComboboxRoot v-model="selectedValue" class="search-combobox" :ignore-filter="true">
            <div class="search-input-row">
              <i class="i-custom-search search-input-icon" aria-hidden="true"></i>
              <ComboboxInput
                v-model="keyword"
                class="search-input"
                placeholder="搜索新闻、产品、研学、视频…"
              />
              <DialogClose class="search-close" aria-label="关闭搜索">
                <span>ESC</span>
              </DialogClose>
            </div>
            <ComboboxContent class="search-content">
              <div v-if="pending" class="search-empty" role="status">
                <i class="i-lucide-loader-circle search-loading-icon" aria-hidden="true"></i>
                <p class="empty-title">正在搜索公开内容...</p>
              </div>
              <div v-else-if="searchError" class="search-empty" role="alert">
                <p class="empty-title">搜索暂时不可用</p>
                <p class="empty-hint">{{ searchError }}</p>
              </div>
              <div v-else-if="keyword && results.length === 0" class="search-empty">
                <p class="empty-title">未找到「{{ keyword }}」相关内容</p>
                <p class="empty-hint">请尝试更短的关键词，或前往对应栏目浏览已发布内容</p>
              </div>
              <ComboboxGroup v-if="!pending && results.length > 0" class="search-group">
                <ComboboxLabel class="search-group-label">搜索结果</ComboboxLabel>
                <ComboboxItem
                  v-for="item in results"
                  :key="`${item.kind}-${item.id}`"
                  :value="item"
                  class="search-item"
                  @select="goTo(item)"
                >
                  <span class="search-item-tag">{{ item.category }}</span>
                  <span class="search-item-title">{{ item.title }}</span>
                  <i class="i-custom-enter search-item-arrow" aria-hidden="true"></i>
                </ComboboxItem>
              </ComboboxGroup>
              <div v-if="!keyword" class="search-hints">
                <i class="i-lucide-search" aria-hidden="true"></i>
                <p>输入关键词搜索新闻、研学、党建、工会、视频、产品与资质证书</p>
              </div>
            </ComboboxContent>
          </ComboboxRoot>
        </DialogContent>
      </Transition>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup lang="ts">
  import type { ArticleItem, CertificateItem, MediaItem, ProductItem } from '~/types/site-api'

  import {
    ComboboxContent,
    ComboboxGroup,
    ComboboxInput,
    ComboboxItem,
    ComboboxLabel,
    ComboboxRoot,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
    DialogTrigger,
  } from 'reka-ui'

  import { getArticlePath, getMediaPath } from '~/utils/content'

  /** 搜索结果项 */
  interface SearchResult {
    category: string
    id: string
    kind: 'article' | 'certificate' | 'media' | 'product'
    path: string
    title: string
  }

  defineOptions({
    name: 'SearchCommand',
  })

  const { getArticles, getCertificates, getMediaItems, getProducts } = useSiteApi()
  const open = shallowRef(false)
  const keyword = shallowRef('')
  const selectedValue = shallowRef<null | SearchResult>(null)
  const results = shallowRef<SearchResult[]>([])
  const pending = shallowRef(false)
  const searchError = shallowRef('')
  let searchSequence = 0

  /** 将文章接口项转换为对应站点栏目的搜索结果。 */
  function mapArticle(article: ArticleItem): SearchResult {
    return {
      id: article.id,
      kind: 'article',
      category: article.category.name,
      title: article.title,
      path: getArticlePath(article),
    }
  }

  /** 将产品接口项转换为商城搜索结果。 */
  function mapProduct(product: ProductItem): SearchResult {
    return {
      id: product.id,
      kind: 'product',
      category: '产品',
      title: product.name,
      path: `/mall/${encodeURIComponent(product.id)}`,
    }
  }

  /** 将资质接口项转换为可直接打开预览的搜索结果。 */
  function mapCertificate(certificate: CertificateItem): SearchResult {
    return {
      id: certificate.id,
      kind: 'certificate',
      category: '资质',
      title: certificate.name,
      path: `/company/qualifications?certificate=${encodeURIComponent(certificate.id)}`,
    }
  }

  /** 将媒体接口项转换为媒体详情搜索结果。 */
  function mapMedia(media: MediaItem): SearchResult {
    return {
      id: media.id,
      kind: 'media',
      category: media.category?.name || (media.kind === 'audio' ? '音频' : '视频'),
      title: media.title,
      path: getMediaPath(media),
    }
  }

  function close() {
    open.value = false
  }

  function goTo(item: SearchResult) {
    close()
    navigateTo(item.path)
  }

  watch([open, keyword], ([isOpen, value], _previous, onCleanup) => {
    const normalizedKeyword = value.trim()
    searchSequence += 1
    const currentSequence = searchSequence
    results.value = []
    searchError.value = ''

    if (!isOpen || !normalizedKeyword) {
      pending.value = false
      return
    }

    pending.value = true
    const timer = setTimeout(async () => {
      try {
        const [articleResponse, productResponse, certificateResponse, mediaResponse] =
          await Promise.all([
            getArticles({ keyword: normalizedKeyword, page: 1, page_limit: 8 }),
            getProducts({ keyword: normalizedKeyword, page: 1, page_limit: 6 }),
            getCertificates({ keyword: normalizedKeyword, page: 1, page_limit: 6 }),
            getMediaItems({ keyword: normalizedKeyword, page: 1, page_limit: 6 }),
          ])
        if (currentSequence !== searchSequence) return
        results.value = [
          ...articleResponse.data.map((article) => mapArticle(article)),
          ...productResponse.data.map((product) => mapProduct(product)),
          ...certificateResponse.data.map((certificate) => mapCertificate(certificate)),
          ...mediaResponse.data.map((media) => mapMedia(media)),
        ]
      } catch {
        if (currentSequence === searchSequence) {
          searchError.value = '请稍后重试，接口恢复后可继续搜索已发布内容。'
        }
      } finally {
        if (currentSequence === searchSequence) pending.value = false
      }
    }, 260)

    onCleanup(() => clearTimeout(timer))
  })

  watch(open, (isOpen) => {
    if (!isOpen) {
      keyword.value = ''
      selectedValue.value = null
      results.value = []
      pending.value = false
      searchError.value = ''
    }
  })
</script>

<style scoped lang="scss">
  @keyframes search-spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* 响应式 */
  @media (max-width: 575.98px) {
    .search-panel {
      margin-top: 8vh;
    }

    .search-close span {
      display: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .search-loading-icon {
      animation: none;
    }
  }

  .search-trigger {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    color: inherit;
    cursor: pointer;
    background: none;
    border: 0;
  }

  /* 遮罩 */
  .search-overlay {
    position: fixed;
    inset: 0;
    z-index: 70;
    background: rgb(15 15 20 / 55%);
    backdrop-filter: blur(4px);
  }

  /* 面板 */
  .search-panel {
    position: fixed;
    top: 0;
    left: 50%;
    z-index: 71;
    width: min(600px, calc(100% - 32px));
    margin-top: 12vh;
    overflow: hidden;
    background: var(--surface);
    border-radius: 16px;
    box-shadow:
      0 0 0 1px rgb(0 0 0 / 3%),
      0 20px 48px rgb(0 0 0 / 16%);
    transform: translateX(-50%);
  }

  .search-combobox {
    display: flex;
    flex-direction: column;
  }

  /* 输入行 */
  .search-input-row {
    display: flex;
    gap: 12px;
    align-items: center;
    padding: 16px 20px;
  }

  .search-input-icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    color: var(--muted);
  }

  .search-input {
    flex: 1;
    min-width: 0;
    font-size: 16px;
    color: var(--ink);
    outline: none;
    background: none;
    border: 0;
  }

  .search-input::placeholder {
    color: var(--muted);
  }

  .search-close {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    padding: 4px 10px;
    font-size: 12px;
    color: var(--muted);
    cursor: pointer;
    background: var(--surface-soft);
    border: 0;
    border-radius: 6px;
    transition:
      color 180ms ease,
      background 180ms ease;
  }

  .search-close:hover {
    color: var(--ink);
    background: var(--line);
  }

  /* 内容区 */
  .search-content {
    max-height: min(440px, 56vh);
    overflow-y: auto;
    border-top: 1px solid var(--line);
  }

  /* 空状态 */
  .search-empty {
    padding: 40px 24px;
    text-align: center;
  }

  .search-loading-icon {
    width: 24px;
    height: 24px;
    margin: 0 auto 12px;
    color: var(--accent);
    animation: search-spin 900ms linear infinite;
  }

  .empty-title {
    margin: 0 0 8px;
    font-size: 14px;
    font-weight: 560;
    color: var(--ink);
  }

  .empty-hint {
    margin: 0;
    font-size: 13px;
    line-height: 1.6;
    color: var(--muted);
  }

  /* 结果列表 */
  .search-group {
    padding: 6px 0;
  }

  .search-group-label {
    padding: 8px 20px 4px;
    font-size: 12px;
    color: var(--muted);
  }

  .search-item {
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 11px 20px;
    color: var(--ink);
    cursor: pointer;
    outline: none;
    background: none;
    border: 0;
    transition: background 120ms ease;
  }

  .search-item[data-highlighted] {
    background: var(--surface-soft);
  }

  .search-item-tag {
    flex-shrink: 0;
    padding: 2px 8px;
    font-size: 12px;
    color: var(--brand);
    background: var(--red-soft);
    border-radius: 4px;
  }

  .search-item-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 14px;
    white-space: nowrap;
  }

  .search-item-arrow {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: var(--muted);
    opacity: 0;
    transition: opacity 160ms ease;
  }

  .search-item[data-highlighted] .search-item-arrow {
    opacity: 1;
  }

  /* 热门搜索 */
  .search-hints {
    display: grid;
    gap: 10px;
    justify-items: center;
    padding: 34px 20px 38px;
    color: var(--muted);
    text-align: center;
  }

  .search-hints i {
    width: 26px;
    height: 26px;
  }

  .search-hints p {
    max-width: 34em;
    margin: 0;
    font-size: 13px;
    line-height: 1.7;
  }

  /* 动画 */
  .overlay-enter-active,
  .overlay-leave-active {
    transition: opacity 200ms ease;
  }

  .overlay-enter-from,
  .overlay-leave-to {
    opacity: 0;
  }

  .content-enter-active {
    transition:
      opacity 220ms ease,
      transform 220ms cubic-bezier(0.16, 1, 0.3, 1);
  }

  .content-leave-active {
    transition:
      opacity 160ms ease,
      transform 160ms ease;
  }

  .content-enter-from {
    opacity: 0;
    transform: translateX(-50%) translateY(-16px) scale(0.96);
  }

  .content-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(-8px) scale(0.98);
  }
</style>
